<template>
  <div>
    <!--  Summary -------------------------------------------------->
    <template v-if="isSummary">
      <div class="columns">
        <div class="column">
          <summary-item :amount="maxAllowableOffer"
            >Maximum<br />Allowed Offer</summary-item
          >
        </div>
        <div class="column">
          <summary-item :amount="record.projected_profit"
            >Retail<br />Profit</summary-item
          >
        </div>
      </div>

      <info-item
        label="Closed Date"
        :value="buyingInfo.buying_info_closing_at"
        is-date
      />
    </template>

    <!--  Expanded -------------------------------------------------->
    <template v-else>
      <info-heading heading="Buying Analysis">
        <div class="flex flex-centered">
          <button-icon
            icon="edit"
            color="is-secondary"
            @click="$refs.retailListingAnalysisModal.open(analysis)"
          />
        </div>
      </info-heading>

      <div v-if="analysis">
        <!-- Need this div to allow css last-of-type to work -->
        <analysis-item label="Retail Value" :value="analysis.retail_value" />
        <analysis-item label="Agent Commission %" :value="analysis.agent_commission" is-percentage/>
        <analysis-item label="Mortgage Payoff" :value="analysis.mortgage_payoff" />
        <analysis-item label="Holding Costs" :value="analysis.holding_costs" />
        <analysis-item label="Other Selling Costs" :value="analysis.other_costs" />
        <analysis-item label="Desired Profit" :value="analysis.desired_profit" />
        <analysis-item label="Maximum Allowed Offer" :value="maxAllowableOffer" is-result />
      </div>
      <hr />

      <profit-item
        type="Retail"
        :amount="record.projected_profit"
        hint="Retail Profit = Retail Value - Mortgage Payoff - Holding Costs - Other Selling Costs - Latest Buying Offer"
      />
      <hr />

      <!-- Dates -->
      <info-heading heading="Dates">
        <button-icon
          icon="edit"
          color="is-secondary"
          @click="$refs.buyingInfoModal.open(buyingInfo)"
        />
      </info-heading>

      <info-item
        label="Accepted Date"
        :value="buyingInfo.buying_info_accepted_at"
        is-date
      />
      <info-item
        label="Contract Date"
        :value="buyingInfo.buying_info_contracted_at"
        is-date
      />
      <info-item
        label="Closed Date"
        :value="buyingInfo.buying_info_closing_at"
        is-date
      />
    </template>

    <modal-container>
      <!-- Buying Info Modal -->
      <modal-crud-form
        ref="buyingInfoModal"
        title="Buying Info Dates"
        form-component="buying-info-form"
        hide-delete-button
        is-triple-column
      />

      <!-- Analysis Modal -->
      <modal-crud-form
        ref="retailListingAnalysisModal"
        title="Analysis"
        form-component="retail-listing-analysis-form"
        hide-delete-button
      />
    </modal-container>
  </div>
</template>

<script>
import InfoHeading from "./InfoHeading.vue";
import ProfitItem from "./ProfitItem.vue";
import InfoItem from "./InfoItem.vue";
import AnalysisItem from "./AnalysisItem.vue";
import SummaryItem from "./SummaryItem.vue";

export default {
  name: "BuyingInfoRetailListingAnalysis",
  components: { SummaryItem, AnalysisItem, InfoItem, ProfitItem, InfoHeading },
  props: {
    isSummary: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    record() {
      return this.$store.state.activeRecord.record;
    },
    analysis() {
      return this.$store.state.activeRecord.retailListingAnalysis;
    },
    buyingInfo() {
      return {
        id: this.record.id,
        record_id: this.record.id,
        buying_info_status: this.record.buying_info_status,

        buying_analysis_id: this.record.buying_analysis_id,

        buying_info_accepted_at: this.record.buying_info_accepted_at,
        buying_info_closing_at: this.record.buying_info_closing_at,
        buying_info_contracted_at: this.record.buying_info_contracted_at,
      };
    },
    worth() {
      if (!this.analysis) return 0;

      const retailValue = this.analysis.retail_value ?? 0;
      const mortgagePayoff = this.analysis.mortgage_payoff ?? 0;
      const holdingCosts = this.analysis.holding_costs ?? 0;
      const otherCosts = this.analysis.other_costs ?? 0;

      const totalCosts = parseFloat(holdingCosts) + parseFloat(mortgagePayoff) + parseFloat(otherCosts);

      return parseFloat(retailValue) - totalCosts;
    },
    maxAllowableOffer() {
      if (!this.analysis) return 0;

      const desiredProfit = this.analysis.desired_profit ?? 0;
      return this.worth - parseFloat(desiredProfit);
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../../styles/variables/variables";

hr {
  margin: 0.75rem (-$modalGutter);
}
</style>
